import clsx from 'clsx';

import { TSource } from '@northladder/services';

import {
  BodySection,
  IBodySectionOptions,
  Field,
  IFieldDescriptionVariant,
} from './BodySection';
import {
  CaptureQuoteContainer,
  ICaptureQuoteContainerOptions,
} from './CaptureQuoteContainer';
import {
  FeedbackSectionApproved,
  IFeedbackSectionApprovedOptions,
} from './FeedbackSectionApproved';
import {
  FeedbackSectionRejected,
  IFeedbackSectionRejectedOptions,
} from './FeedbackSectionRejected';
import { Footer, IFooterProps } from './Footer';
import { FormSection, IFormSectionProps } from './FormSection';
import {
  TransactionHeader,
  ITransactionHeaderOptions,
} from './TransactionHeader';

export interface ITransactionSnapshotProps {
  bodySection?: IBodySectionOptions;
  captureQuoteContainer?: ICaptureQuoteContainerOptions;
  feedbackSectionApproved?: IFeedbackSectionApprovedOptions;
  feedbackSectionRejected?: IFeedbackSectionRejectedOptions;
  footer?: IFooterProps;
  formSection?: IFormSectionProps;
  header: ITransactionHeaderOptions;
  sectionDescription?: Omit<IFieldDescriptionVariant, 'type'>;
  source: TSource;
}

/**
 * -----------------------------------------------------------------------------
 * It renders a transaction snapshot which contains a header, body and footer
 */
export function TransactionSnapshot({
  bodySection,
  captureQuoteContainer,
  feedbackSectionApproved,
  feedbackSectionRejected,
  footer,
  formSection,
  header,
  sectionDescription,
  source,
}: Readonly<ITransactionSnapshotProps>) {
  const activeClassNames =
    source === 'user-active'
      ? 'bg-white border border-white'
      : 'bg-gray-50 border border-gray-200';

  return (
    <div className={clsx('rounded-xl py-1.5', activeClassNames)}>
      <TransactionHeader {...header} />

      {sectionDescription ? (
        <div className="px-4 pb-1.5">
          <Field
            variant={{
              label: sectionDescription?.label || '',
              text: sectionDescription.text || '',
              type: 'description',
            }}
          />
        </div>
      ) : null}

      {bodySection ? <BodySection {...bodySection} /> : null}

      {captureQuoteContainer ? (
        <CaptureQuoteContainer {...captureQuoteContainer} />
      ) : null}

      {formSection ? <FormSection {...formSection} /> : null}

      {feedbackSectionApproved ? (
        <FeedbackSectionApproved {...feedbackSectionApproved} />
      ) : null}

      {feedbackSectionRejected ? (
        <FeedbackSectionRejected {...feedbackSectionRejected} />
      ) : null}

      {footer ? <Footer {...footer} /> : null}
    </div>
  );
}
